import { useCallback, useState, useEffect } from 'react'

import { getInfoAboutService, initializeUsercentricsListeners } from 'utils/getInfoAboutService'

const SCRIPT_ID = 'intercomFinAIAgent'
const USERCENTRICS_INTERCOM_NAME = 'Intercom'

const useInternalIntercomUserConsent = () => {
  const [usercentricsServicesInfo, setUsercentircsServicesInfo] = useState(
    getInfoAboutService(USERCENTRICS_INTERCOM_NAME),
  )
  const [isProvided, setIsProvided] = useState(
    usercentricsServicesInfo[USERCENTRICS_INTERCOM_NAME]?.status ?? false,
  )

  useEffect(() => {
    const updateConsent = () => {
      const info = getInfoAboutService(USERCENTRICS_INTERCOM_NAME)
      setUsercentircsServicesInfo(info)
      setIsProvided(info[USERCENTRICS_INTERCOM_NAME]?.status ?? false)
    }
    initializeUsercentricsListeners(updateConsent)
    updateConsent()
  }, [])

  return isProvided
}

export const useIntercomFinAIAgentWidget = () => {
  const hasIntercomConsent = useInternalIntercomUserConsent()
  const [isIntercomLoaded, setIsIntercomLoaded] = useState(false)

  const initializeIntercom = useCallback(() => {
    const intercomFunction = function () {
      // eslint-disable-next-line prefer-rest-params
      intercomFunction.c(arguments)
    } as unknown as {
      (args: unknown): void
      q: unknown[]
      c: (args: unknown) => void
    }

    // eslint-disable-next-line id-length
    intercomFunction.q = []
    // eslint-disable-next-line id-length
    intercomFunction.c = function (args: unknown) {
      intercomFunction.q.push(args)
    }

    window.intercomSettings = window.intercomFinAISettings
    window.IntercomFinAIAgent = intercomFunction
    window.IntercomFinAIAgent('boot', window.intercomSettings)
    setIsIntercomLoaded(true)
  }, [])

  const loadScript = useCallback(() => {
    const appId = window?.intercomFinAISettings?.app_id
    if (!appId) {
      return
    }

    const existingScript = document.getElementById(SCRIPT_ID)

    if (existingScript) {
      return
    }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.id = SCRIPT_ID
    script.src = `https://widget.intercom.io/widget/${appId}`
    document.head.appendChild(script)

    script.onload = () => {
      initializeIntercom()
    }

    script.onerror = () => {
      setIsIntercomLoaded(false)
      console.error('Failed to load Intercom script')
    }
  }, [initializeIntercom])

  const updateIntercom = useCallback((intercomFinAIAgent: Window['IntercomFinAIAgent']) => {
    if (typeof intercomFinAIAgent !== 'function') {
      return false
    }

    window.intercomSettings = window.intercomFinAISettings
    intercomFinAIAgent('reattach_activator')
    intercomFinAIAgent('update', window.intercomSettings)

    return true
  }, [])

  const loadIntercomFinAIAgent = useCallback(() => {
    const intercomFinAIAgent = window.IntercomFinAIAgent
    if (updateIntercom(intercomFinAIAgent)) {
      return
    }
    loadScript()
  }, [loadScript, updateIntercom])

  useEffect(() => {
    const onLoad = () => loadIntercomFinAIAgent()

    if (hasIntercomConsent) {
      if (document.readyState === 'complete') {
        loadIntercomFinAIAgent()
      } else {
        window.addEventListener('load', onLoad, { once: true })
      }
    }

    return () => {
      if (typeof window.IntercomFinAIAgent === 'function') {
        window.IntercomFinAIAgent = undefined
      }

      const existingScript = document.getElementById(SCRIPT_ID)
      if (existingScript) {
        existingScript.remove()
      }
      setIsIntercomLoaded(false)
    }
  }, [hasIntercomConsent, loadIntercomFinAIAgent])

  return {
    isIntercomFinAIEnabled: hasIntercomConsent,
    isIntercomLoaded,
  }
}
